import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { jobsApi } from '../services/api/jobs';
import type { Job } from '../types/job';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import { useAuth } from '../contexts/AuthContext';
import { XMarkIcon, BuildingOfficeIcon, MapPinIcon, CalendarIcon, BriefcaseIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import JobFilters, { JobFilters as JobFiltersType } from '../components/jobs/JobFilters';
import JobList from '../components/jobs/JobList';
import { Dialog } from '@headlessui/react';
import { toast as sonnerToast } from 'sonner';

const getJobCountMessage = (count: number) => {
  if (count === 0) {
    return "Start your search - new opportunities are added daily!";
  }

  // Convert exact number to range or descriptive phrase
  const getRange = (count: number) => {
    if (count < 10) return "A handful of";
    if (count < 25) return "Dozens of";
    if (count < 50) return "Many";
    if (count < 100) return "Numerous";
    if (count < 200) return "Hundreds of";
    return "Countless";
  };

  const messages = [
    `${getRange(count)} doors to your next adventure`,
    `${getRange(count)} career possibilities await`,
    `A world of opportunities at your fingertips`,
    `Your dream job is in the mix`,
    `Fresh opportunities every day`,
    `Your next big move is here`,
    `Discover your perfect role`,
    `Career-changing opportunities await`,
    `Take the next step in your journey`,
    `Find your dream role today`
  ];

  return messages[Math.floor(Math.random() * messages.length)];
};

// Add new interface for job creation form
interface JobCreateForm {
  title: string;
  company: string;
  description: string;
  url: string;
  location: string;
  salary_min?: number;
  salary_max?: number;
  salary_currency?: string;
  salary_period?: string;
  remote: boolean;
  salary: string;
}

const JobsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [notInterestedJobs, setNotInterestedJobs] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<JobFiltersType>(() => {
    const savedFilters = localStorage.getItem('jobFilters');
    if (savedFilters) {
      return JSON.parse(savedFilters);
    }
    return {
      sortBy: 'recent',
      title: undefined,
      company: undefined,
      location: undefined
    };
  });
  const jobsPerPage = 15;
  const [filtering, setFiltering] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState<JobCreateForm>({
    title: '',
    company: '',
    description: '',
    url: '',
    location: '',
    remote: false,
    salary: ''
  });

  // Update localStorage when filters change
  useEffect(() => {
    localStorage.setItem('jobFilters', JSON.stringify(filters));
  }, [filters]);

  const fetchJobs = useCallback(async () => {
    try {
      if (!filtering) {
        setLoading(true);
      }
      const skip = (currentPage - 1) * jobsPerPage;
      const response = await jobsApi.getJobs(skip, jobsPerPage, filters);
      setJobs(response.jobs);
      setTotal(response.total);
    } catch (err: any) {
      console.error('Failed to fetch jobs:', err);
      setError(err.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
      setFiltering(false);
    }
  }, [currentPage, filters, jobsPerPage, filtering]);

  useEffect(() => {
    if (!user) return;
    fetchJobs();
  }, [user, fetchJobs]);

  const handleJobClick = useCallback((jobId: number) => {
    navigate(`/jobs/${jobId}`);
  }, [navigate]);

  const handleNotInterested = useCallback(async (jobId: number) => {
    if (!user) return;
    
    setNotInterestedJobs(prev => [...prev, jobId]);
    setJobs(prev => prev.filter(job => job.id !== jobId));
    setTotal(prev => Math.max(0, prev - 1));

    try {
      await jobsApi.markJobNotInterested(jobId, user.id);
      toast.success('Job removed from your list', {
        duration: 3000,
        style: { background: '#10B981', color: 'white' }
      });
    } catch (error) {
      setNotInterestedJobs(prev => prev.filter(id => id !== jobId));
      await fetchJobs();
      toast.error('Failed to update job status', {
        duration: 3000,
        style: { background: '#EF4444', color: 'white' }
      });
    }
  }, [user, fetchJobs]);

  const handleUndoNotInterested = async (jobId: number) => {
    if (!user) return;

    try {
      await jobsApi.removeJobNotInterested(jobId, user.id);
      setNotInterestedJobs(prev => prev.filter(id => id !== jobId));
      await fetchJobs();
      toast.success('Job restored successfully', {
        duration: 3000,
        style: { background: '#10B981', color: 'white' }
      });
    } catch (error) {
      toast.error('Failed to restore job', {
        duration: 3000,
        style: { background: '#EF4444', color: 'white' }
      });
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / jobsPerPage));

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
    window.scrollTo(0, 0);
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
    window.scrollTo(0, 0);
  };

  const handleFilterChange = useCallback((newFilters: JobFiltersType) => {
    setCurrentPage(1);
    setFiltering(true);
    const cleanedFilters = Object.fromEntries(
      Object.entries(newFilters).filter(([_, value]) => value !== undefined && value !== '')
    ) as JobFiltersType;
    setFilters(cleanedFilters);
  }, []);

  const handleFilterReset = useCallback(() => {
    const defaultFilters: JobFiltersType = {
      sortBy: 'recent' as const,
      title: undefined,
      company: undefined,
      location: undefined
    };
    setFilters(defaultFilters);
    localStorage.removeItem('jobFilters');
    setCurrentPage(1);
    setFiltering(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchJobs();
    }, 300);

    return () => clearTimeout(timer);
  }, [filters, currentPage, fetchJobs]);

  // Add handler for job creation
  const handleCreateJob = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await jobsApi.createJob(createFormData);
      setIsCreateModalOpen(false);
      setCreateFormData({
        title: '',
        company: '',
        description: '',
        url: '',
        location: '',
        remote: false,
        salary: ''
      });
      sonnerToast.success('Job posted successfully');
      fetchJobs(); // Refresh the job list
    } catch (error: any) {
      sonnerToast.error(error.message || 'Failed to create job');
    }
  };

  const headerContent = useMemo(() => (
    <motion.div 
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex justify-between items-center mb-12"
    >
      <div className="flex flex-col items-center justify-center space-y-4 w-full">
        <div className="bg-blue-600 p-3 rounded-2xl shadow-lg">
          <BriefcaseIcon className="h-8 w-8 text-white" />
        </div>
        <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent text-center">
          {getJobCountMessage(total)}
        </h1>
      </div>
    </motion.div>
  ), [total]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <LoadingSpinner className="w-12 h-12 text-blue-600" />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} className="mt-8" />;
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-28">
      <div className="container mx-auto px-4 pb-20">
        {headerContent}
        
        {/* Add Create Job Button */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex justify-end"
        >
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Add Job</span>
          </button>
        </motion.div>

        {/* Create Job Modal */}
        <Dialog
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          className="fixed inset-0 z-50 overflow-y-auto"
        >
          <div className="flex min-h-screen items-center justify-center p-4">
            <Dialog.Overlay className="fixed inset-0 bg-black/30" />

            <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full p-6">
              <Dialog.Title className="text-2xl font-bold mb-6">Create Your Private Job Listing</Dialog.Title>

              <form onSubmit={handleCreateJob} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      required
                      value={createFormData.title}
                      onChange={e => setCreateFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                    <input
                      type="text"
                      required
                      value={createFormData.company}
                      onChange={e => setCreateFormData(prev => ({ ...prev, company: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <input
                      type="text"
                      required
                      value={createFormData.location}
                      onChange={e => setCreateFormData(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                    <input
                      type="url"
                      required
                      value={createFormData.url}
                      onChange={e => setCreateFormData(prev => ({ ...prev, url: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      required
                      value={createFormData.description}
                      onChange={e => setCreateFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={4}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Salary Range</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={createFormData.salary_min || ''}
                        onChange={e => setCreateFormData(prev => ({ ...prev, salary_min: Number(e.target.value) }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <span>-</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={createFormData.salary_max || ''}
                        onChange={e => setCreateFormData(prev => ({ ...prev, salary_max: Number(e.target.value) }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Salary Details</label>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={createFormData.salary_currency || ''}
                        onChange={e => setCreateFormData(prev => ({ ...prev, salary_currency: e.target.value }))}
                        className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Currency</option>
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                        <option value="GBP">GBP</option>
                      </select>
                      <select
                        value={createFormData.salary_period || ''}
                        onChange={e => setCreateFormData(prev => ({ ...prev, salary_period: e.target.value }))}
                        className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Period</option>
                        <option value="yearly">Yearly</option>
                        <option value="monthly">Monthly</option>
                        <option value="hourly">Hourly</option>
                      </select>
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={createFormData.remote}
                        onChange={e => setCreateFormData(prev => ({ ...prev, remote: e.target.checked }))}
                        className="rounded text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Remote Position</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setIsCreateModalOpen(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Create Job
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Dialog>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <JobFilters 
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleFilterReset}
          />
        </motion.div>

        <AnimatePresence>
          {loading && !filtering && (
            <div className="flex justify-center items-center min-h-[400px]">
              <LoadingSpinner className="w-12 h-12 text-blue-600" />
            </div>
          )}
        </AnimatePresence>

        {error && (
          <ErrorMessage message={error} />
        )}

        {!loading && !error && (
          <AnimatePresence mode="wait">
            {Array.isArray(jobs) && jobs.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="text-center py-16 bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl"
              >
                <p className="text-gray-500 text-lg">No jobs found. Try searching for jobs first.</p>
              </motion.div>
            ) : (
              <>
                {filtering && (
                  <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-10">
                    <LoadingSpinner className="w-8 h-8 text-blue-600" />
                  </div>
                )}
                <div className={filtering ? 'opacity-50' : ''}>
                  <JobList
                    jobs={jobs}
                    onJobClick={handleJobClick}
                    onNotInterested={handleNotInterested}
                  />
                </div>
              </>
            )}
          </AnimatePresence>
        )}

        {totalPages > 1 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-12 flex justify-center items-center space-x-6"
          >
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className={`p-3 rounded-xl transition-colors duration-300 ${
                currentPage === 1 
                  ? 'text-gray-300 cursor-not-allowed' 
                  : 'text-gray-600 hover:bg-white/80 hover:shadow-lg'
              }`}
            >
              <ChevronLeftIcon className="h-6 w-6" />
            </motion.button>
            <span className="text-gray-700 font-semibold bg-white/80 px-6 py-3 rounded-xl shadow-md">
              Page {currentPage} of {totalPages}
            </span>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`p-3 rounded-xl transition-colors duration-300 ${
                currentPage === totalPages 
                  ? 'text-gray-300 cursor-not-allowed' 
                  : 'text-gray-600 hover:bg-white/80 hover:shadow-lg'
              }`}
            >
              <ChevronRightIcon className="h-6 w-6" />
            </motion.button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default JobsPage;